<script setup>
import { computed, onMounted, ref } from 'vue';
import { useAlert } from 'dashboard/composables';
import CostReportsAPI from 'dashboard/api/costReports';

const isLoading = ref(false);
const report = ref({
  generated_at: null,
  periods: {},
  summary: {},
  accounts: [],
});

const fetchReport = async () => {
  isLoading.value = true;

  try {
    const { data } = await CostReportsAPI.get();
    report.value = data;
  } catch {
    useAlert('Nao foi possivel carregar o relatorio de custos da Meta.');
  } finally {
    isLoading.value = false;
  }
};

const formatMoney = (value, currency) => {
  const amount = Number(value || 0);
  const code = currency || report.value.summary?.currency || 'USD';

  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: code,
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  }).format(amount);
};

const formatNumber = value =>
  new Intl.NumberFormat('pt-BR').format(Number(value || 0));

const formatDate = value => {
  if (!value) return '-';

  return new Date(value).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

const currentPeriod = computed(() => report.value.periods?.current || {});
const previousPeriod = computed(() => report.value.periods?.previous || {});

const variation = computed(() => {
  const current = Number(report.value.summary?.current_cost || 0);
  const previous = Number(report.value.summary?.previous_cost || 0);
  if (!previous) return null;

  return ((current - previous) / previous) * 100;
});

const summaryCards = computed(() => [
  {
    label: 'Fatura atual Meta',
    value: formatMoney(report.value.summary?.current_cost),
    detail: `${formatDate(currentPeriod.value.from)} ate ${formatDate(currentPeriod.value.to)}`,
  },
  {
    label: 'Fatura anterior Meta',
    value: formatMoney(report.value.summary?.previous_cost),
    detail: `${formatDate(previousPeriod.value.from)} ate ${formatDate(previousPeriod.value.to)}`,
  },
  {
    label: 'Mensagens atuais',
    value: formatNumber(report.value.summary?.current_conversations),
    detail: 'Base Meta pricing_analytics',
  },
  {
    label: 'Variacao',
    value:
      variation.value === null
        ? '-'
        : `${variation.value > 0 ? '+' : ''}${variation.value.toFixed(1)}%`,
    detail: 'Atual vs. anterior',
  },
]);

const rows = computed(() =>
  report.value.accounts.flatMap(account =>
    account.rows.map(row => ({
      ...row,
      waba_id: account.waba_id,
      inboxes: account.inboxes,
      error: account.error,
    }))
  )
);

const accountHasError = computed(() =>
  report.value.accounts.some(account => account.error)
);

onMounted(fetchReport);
</script>

<template>
  <main class="flex flex-col w-full min-h-screen bg-n-background">
    <header
      class="flex items-start justify-between gap-4 px-8 py-6 border-b border-n-weak"
    >
      <div>
        <h1 class="text-2xl font-semibold text-n-slate-12">Custos Meta</h1>
        <p class="mt-1 text-sm text-n-slate-11">
          Custo atual e anterior puxado da API de pricing analytics do WhatsApp Business.
        </p>
      </div>
      <button
        class="inline-flex items-center h-10 gap-2 px-3 text-sm font-medium border rounded-md border-n-strong bg-n-alpha-2 text-n-slate-12 hover:bg-n-alpha-3"
        @click="fetchReport"
      >
        <span class="i-lucide-refresh-cw size-4" />
        Atualizar
      </button>
    </header>

    <section class="flex flex-col gap-6 p-8">
      <div
        v-if="accountHasError"
        class="rounded-md border border-n-amber-5 bg-n-amber-2 px-4 py-3 text-sm text-n-amber-12"
      >
        Algumas conexões não retornaram custos. Verifique se o token Meta tem
        permissão de analytics do WhatsApp Business.
      </div>

      <div class="grid grid-cols-1 gap-3 md:grid-cols-4">
        <div
          v-for="card in summaryCards"
          :key="card.label"
          class="p-4 border rounded-lg border-n-weak bg-n-alpha-2"
        >
          <p class="text-xs font-medium uppercase text-n-slate-10">
            {{ card.label }}
          </p>
          <p class="mt-2 text-2xl font-semibold text-n-slate-12">
            {{ card.value }}
          </p>
          <p class="mt-1 text-xs text-n-slate-10">
            {{ card.detail }}
          </p>
        </div>
      </div>

      <div
        v-if="isLoading"
        class="flex items-center justify-center h-40 text-sm text-n-slate-11"
      >
        <span class="i-lucide-loader-2 mr-2 size-5 animate-spin" />
        Carregando custos da Meta
      </div>

      <template v-else>
        <section class="overflow-hidden border rounded-lg border-n-weak">
          <div class="px-4 py-3 border-b border-n-weak bg-n-alpha-2">
            <h2 class="text-base font-semibold text-n-slate-12">
              Custos por conexão
            </h2>
          </div>
          <div class="overflow-x-auto">
            <table class="w-full text-sm">
              <thead class="text-left bg-n-alpha-2 text-n-slate-10">
                <tr>
                  <th class="px-4 py-3 font-medium">Conexão</th>
                  <th class="px-4 py-3 font-medium">WABA</th>
                  <th class="px-4 py-3 font-medium">Atual</th>
                  <th class="px-4 py-3 font-medium">Anterior</th>
                  <th class="px-4 py-3 font-medium">Mensagens atuais</th>
                  <th class="px-4 py-3 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="account in report.accounts"
                  :key="account.waba_id"
                  class="border-t border-n-weak text-n-slate-12"
                >
                  <td class="px-4 py-3">
                    <div
                      v-for="inbox in account.inboxes"
                      :key="inbox.id"
                      class="leading-5"
                    >
                      <span class="font-medium">{{ inbox.name }}</span>
                      <span class="ml-1 text-xs text-n-slate-10">
                        {{ inbox.phone_number }}
                      </span>
                    </div>
                  </td>
                  <td class="px-4 py-3 font-mono text-xs">
                    {{ account.waba_id }}
                  </td>
                  <td class="px-4 py-3 font-medium">
                    {{
                      formatMoney(
                        account.periods.current.cost,
                        account.periods.current.currency
                      )
                    }}
                  </td>
                  <td class="px-4 py-3">
                    {{
                      formatMoney(
                        account.periods.previous.cost,
                        account.periods.previous.currency
                      )
                    }}
                  </td>
                  <td class="px-4 py-3">
                    {{ formatNumber(account.periods.current.conversations) }}
                  </td>
                  <td class="px-4 py-3">
                    <span
                      v-if="account.error"
                      class="rounded-md bg-n-ruby-3 px-2 py-1 text-xs text-n-ruby-11"
                    >
                      {{ account.error }}
                    </span>
                    <span
                      v-else
                      class="rounded-md bg-n-teal-3 px-2 py-1 text-xs text-n-teal-11"
                    >
                      Dados carregados
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <section class="overflow-hidden border rounded-lg border-n-weak">
          <div class="px-4 py-3 border-b border-n-weak bg-n-alpha-2">
            <h2 class="text-base font-semibold text-n-slate-12">
              Detalhamento Meta
            </h2>
          </div>
          <div class="overflow-x-auto">
            <table class="w-full text-sm">
              <thead class="text-left bg-n-alpha-2 text-n-slate-10">
                <tr>
                  <th class="px-4 py-3 font-medium">Período</th>
                  <th class="px-4 py-3 font-medium">Categoria</th>
                  <th class="px-4 py-3 font-medium">Tipo</th>
                  <th class="px-4 py-3 font-medium">Direção</th>
                  <th class="px-4 py-3 font-medium">Telefone/País</th>
                  <th class="px-4 py-3 font-medium">Mensagens</th>
                  <th class="px-4 py-3 font-medium">Custo</th>
                  <th class="px-4 py-3 font-medium">WABA</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="row in rows"
                  :key="`${row.waba_id}-${row.period}-${row.conversation_type}-${row.conversation_direction}`"
                  class="border-t border-n-weak text-n-slate-12"
                >
                  <td class="px-4 py-3">
                    {{ row.period === 'current' ? 'Atual' : 'Anterior' }}
                  </td>
                  <td class="px-4 py-3">
                    {{ row.conversation_category || '-' }}
                  </td>
                  <td class="px-4 py-3">{{ row.conversation_type }}</td>
                  <td class="px-4 py-3">
                    {{ row.conversation_direction || '-' }}
                  </td>
                  <td class="px-4 py-3">
                    {{ row.phone_number || '-' }}
                    <span v-if="row.country" class="text-xs text-n-slate-10">
                      {{ row.country }}
                    </span>
                  </td>
                  <td class="px-4 py-3">
                    {{ formatNumber(row.conversations) }}
                  </td>
                  <td class="px-4 py-3 font-medium">
                    {{ formatMoney(row.cost, row.currency) }}
                  </td>
                  <td class="px-4 py-3 font-mono text-xs">
                    {{ row.waba_id }}
                  </td>
                </tr>
                <tr v-if="!rows.length">
                  <td colspan="8" class="px-4 py-10 text-center text-n-slate-10">
                    Nenhum custo retornado pela Meta para o período.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <p class="text-xs text-n-slate-10">
          Observação: a Meta retorna custos via pricing analytics do WhatsApp Business.
          O valor pode diferir de uma fatura final caso existam ajustes,
          impostos, créditos ou cobranças fora do WhatsApp.
        </p>
      </template>
    </section>
  </main>
</template>
